import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Navigate } from "react-router-dom";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { assignFdeRole, revokeFdeRole } from "services/fde/fdeService";
import {
  listGlobalTciManagers,
  lookupUserUidByEmail,
} from "services/fde/globalTciManagerService";
import { fetchUserLabels } from "services/fde/tciManagerService";

const GlobalTciManagersPage = () => {
  const { isGlobalAdmin, userLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [userNames, setUserNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const assignments = await listGlobalTciManagers();
      const labels = await fetchUserLabels(assignments.map((row) => row.uid));
      setRows(assignments);
      setUserNames(labels);
    } catch (err) {
      setError(err?.message || "Failed to load Global TCI Managers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userLoading && isGlobalAdmin) loadRows();
  }, [userLoading, isGlobalAdmin, loadRows]);

  const handleAssign = async () => {
    setFormError(null);
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      setFormError("Enter a valid email.");
      return;
    }

    setSubmitting(true);
    try {
      const uid = await lookupUserUidByEmail(normalizedEmail);
      if (!uid) {
        setFormError("No user found with that email.");
        return;
      }

      await assignFdeRole({
        uid,
        role: "global-tci-manager",
        scopeLevel: "platform",
        scopeId: "platform",
        trainingComplete: true,
      });

      setSuccess("Global TCI Manager assigned.");
      setDialogOpen(false);
      setEmail("");
      await loadRows();
    } catch (err) {
      setFormError(err?.message || "Failed to assign Global TCI Manager.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError(null);
    setSuccess(null);
    try {
      await revokeFdeRole({ assignmentId: revokeTarget.id });
      setSuccess("Global TCI Manager revoked.");
      setRevokeTarget(null);
      await loadRows();
    } catch (err) {
      setError(err?.message || "Failed to revoke Global TCI Manager.");
    } finally {
      setRevoking(false);
    }
  };

  if (!userLoading && !isGlobalAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Global TCI Managers"
        subtitle="Assign platform-level stewards who can review institutional sharing requests and manage the global TCI catalog."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Button variant="contained" sx={{ mb: 2 }} onClick={() => setDialogOpen(true)}>
        Assign Global TCI Manager
      </Button>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{userNames[row.uid] || row.uid}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align="right">
                    <LoadingButton color="error" onClick={() => setRevokeTarget(row)}>
                      Revoke
                    </LoadingButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary">No Global TCI Managers assigned yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign Global TCI Manager</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The user must already have a FusionMSI account. This role grants access to the global
            TCI catalog and sharing review queue, not full platform admin authority.
          </Typography>
          <TextField
            label="User email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
          />
          {formError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton loading={submitting} variant="contained" onClick={handleAssign}>
            Assign
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title="Revoke Global TCI Manager?"
        message={`Revoke access for ${userNames[revokeTarget?.uid] || revokeTarget?.uid}?`}
        confirmColor="error"
        loading={revoking}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
      />
    </Box>
  );
};

export default GlobalTciManagersPage;
